import { epsgLabel } from '../constants/epsg';
import { crsDefinition } from './crs';

// What the Point Cloud page says about a file's grid (the coordinate system
// its LAS/LAZ header named, stored at conversion as File.metadata.srs). Shown
// only when the file named one; a warning when it is not the project's grid
// or is in degrees, so whoever opens the page sees a file that may not line up
// with the others or the base map.

export interface FileGridSrs { epsg?: number | null; projected?: boolean | null }
export interface FileGridNote { text: string; title: string; warn: boolean }

// Two codes for one grid (a deprecated Balkans code and its successor, Serbia's
// SRB_ETRS89 and ETRS89 UTM 34N) are not a mismatch.
const sameGrid = (a: number, b: number): boolean => {
  if (a === b) return true;
  const da = crsDefinition(a)?.proj4;
  return !!da && da === crsDefinition(b)?.proj4;
};

export function fileGridNote(srs: FileGridSrs | null | undefined, projectEpsg: number | null | undefined): FileGridNote | null {
  if (!srs) return null;
  if (srs.projected === false) {
    return {
      text: srs.epsg ? `EPSG:${srs.epsg} · degrees` : 'Latitude/longitude',
      title: 'This file\'s coordinates are latitude and longitude in degrees, not metres on a grid: the Scene cannot place it on the base map, and it will not line up with files on a grid.',
      warn: true,
    };
  }
  if (!srs.epsg) return null;
  const name = epsgLabel(srs.epsg);
  if (projectEpsg && !sameGrid(srs.epsg, projectEpsg)) {
    return {
      text: `EPSG:${srs.epsg} · project uses EPSG:${projectEpsg}`,
      title: `This file names ${name}, but the project is set to ${epsgLabel(projectEpsg)}. Its coordinates may not line up with the other files or the base map. Check which grid the survey used.`,
      warn: true,
    };
  }
  const projectsGrid = projectEpsg === srs.epsg
    ? `${name} — the project's grid`
    : `${name} — the same grid as the project's EPSG:${projectEpsg} under another code`;
  return {
    text: `EPSG:${srs.epsg}`,
    title: projectEpsg ? projectsGrid : `${name}. The project has no grid set yet.`,
    warn: false,
  };
}
